import React, { createContext, useContext, useState, useCallback } from "react";
import {
  doc,
  collection,
  writeBatch,
  getDoc,
  getDocs,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";
import { useUser } from "./UserContext";
import { uploadCloudApi } from "../utils/cloudApi";
import { componentsToColor } from "pdf-lib";

const LogsContext = createContext();

export const useLogsContext = () => {
  const context = useContext(LogsContext);
  if (!context) {
    throw new Error("useLogsContext must be used within a LogsContextProvider");
  }
  return context;
};

// Configuration for different log types
const LOG_TYPE_CONFIG = {
  "secretarial-records": {
    collectionName: "secretaryLogs",
    displayName: "secretarial records",
    dropdownListKeys: ["secretaryList", "assistedList"],
  },
  "process-server-logs": {
    collectionName: "serverLogs",
    displayName: "process server logs",
    dropdownListKeys: ["processedByList", "assistedList", "agencyList"],
  },
  "exec-admin": {
    collectionName: "execAdmin",
    displayName: "executive & administrative",
    dropdownListKeys: ["processedByList", "assistedList"],
  },
  "case-brief": {
    collectionName: "caseBrief",
    displayName: "Case Brief",
    dropdownListKeys: ["processedByList", "assistedList"],
  },
};

export const LogsContextProvider = ({ children }) => {
  const { userData } = useUser();
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  // Centralized state for logs and templates
  const [logsCache, setLogsCache] = useState({});
  const [templatesCache, setTemplatesCache] = useState({});

  // Get cached logs for a specific user and type
  const getCachedLogs = useCallback(
    (userId, logType) => {
      const key = `${userId}_${logType}`;
      return logsCache[key] || null;
    },
    [logsCache]
  );

  // Get cached templates for a specific user
  const getCachedTemplates = useCallback(
    (userId) => {
      return templatesCache[userId] || null;
    },
    [templatesCache]
  );

  // Update logs cache
  const updateLogsCache = useCallback((userId, logType, data) => {
    const key = `${userId}_${logType}`;
    setLogsCache((prev) => ({
      ...prev,
      [key]: data,
    }));
  }, []);

  // Update templates cache
  const updateTemplatesCache = useCallback((userId, templates) => {
    setTemplatesCache((prev) => ({
      ...prev,
      [userId]: templates,
    }));
  }, []);

  // Get templates for a specific user (from cache or empty array)
  const getTemplates = useCallback(
    (userId) => {
      return templatesCache[userId] || [];
    },
    [templatesCache]
  );

  // Save or update a template
  const saveTemplate = async (userId, templateData) => {
    try {
      setLoading(true);
      setLoadingText("Saving template...");

      const formData = { ...templateData };

      const uploadFields = ["logo", "footer"];

      const uploadPromises = uploadFields
        .map((field) => {
          if (formData[field] instanceof File) {
            return uploadCloudApi(formData[field], userId, "img").then(
              (url) => ({ field, url })
            );
          }
          return null;
        })
        .filter(Boolean);

      const uploadResults = await Promise.all(uploadPromises);

      uploadResults.forEach(({ field, url }) => {
        formData[field] = url;
      });

      const templatesRef = collection(db, "logs", userId, "logTemplates");

      let savedTemplate;

      if (templateData.id && templateData.id.startsWith("template_")) {
        // Create new template (strip the temporary id)
        const { id, ...templateDataWithoutId } = templateData;

        const docRef = await addDoc(templatesRef, {
          ...templateDataWithoutId,
          logo: formData.logo || null,
          footer: formData.footer || null,
          userId: userId,
          createdAt: new Date().toISOString(),
        });

        savedTemplate = {
          ...templateData,
          id: docRef.id,
          logo: formData.logo || null,
          footer: formData.footer || null,
        };

        toast.success("Template created successfully");
      } else {
        // Update existing template
        await updateDoc(
          doc(db, "logs", userId, "logTemplates", templateData.id),
          {
            ...templateData,
            logo: formData.logo || null,
            footer: formData.footer || null,
            updatedAt: new Date().toISOString(),
          }
        );

        savedTemplate = {
          ...templateData,
          logo: formData.logo || null,
          footer: formData.footer || null,
        };

        toast.success("Template updated successfully");
      }

      // Update cache
      const cached = getCachedTemplates(userId);
      if (cached) {
        const updatedTemplates = templateData.id.startsWith("template_")
          ? [...cached, savedTemplate]
          : cached.map((t) => (t.id === savedTemplate.id ? savedTemplate : t));
        updateTemplatesCache(userId, updatedTemplates);
      } else {
        // If no cache, just add this template
        updateTemplatesCache(userId, [savedTemplate]);
      }

      return { success: true, data: savedTemplate };
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
      return { success: false, error };
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  // Delete a template
  const deleteTemplate = async (userId, templateId) => {
    try {
      setLoading(true);
      setLoadingText("Deleting template...");

      await deleteDoc(doc(db, "logs", userId, "logTemplates", templateId));

      toast.success("Template deleted successfully");

      // Update cache
      const cached = getCachedTemplates(userId);
      if (cached) {
        const updatedTemplates = cached.filter((t) => t.id !== templateId);
        updateTemplatesCache(userId, updatedTemplates);
      }

      return { success: true };
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
      return { success: false, error };
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  // Load templates
  const loadTemplates = useCallback(
    async (userId) => {
      // Check cache first
      const cached = getCachedTemplates(userId);
      if (cached) {
        return { success: true, data: cached };
      }

      try {
        setLoading(true);
        setLoadingText("Loading templates...");

        const templatesRef = collection(db, "logs", userId, "logTemplates");
        const snapshot = await getDocs(templatesRef);
        const loadedTemplates = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Update cache
        updateTemplatesCache(userId, loadedTemplates);

        return { success: true, data: loadedTemplates };
      } catch (error) {
        console.error("Error loading templates:", error);
        return { success: false, error };
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    },
    [getCachedTemplates, updateTemplatesCache]
  );

  // Generic save function for all log types
  const saveLogs = async (userId, logType, data) => {
    try {
      const config = LOG_TYPE_CONFIG[logType];
      if (!config) {
        throw new Error(`Unknown log type: ${logType}`);
      }

      setLoading(true);
      setLoadingText(`Saving ${config.displayName}...`);

      const batch = writeBatch(db);
      let operationCount = 0;

      // Save dropdown lists to main logs document (only if provided)
      if (data.dropdownLists && Object.keys(data.dropdownLists).length > 0) {
        const logsDocRef = doc(db, "logs", userId);
        const dropdownData = {};

        // Only include dropdown lists that were actually provided
        Object.keys(data.dropdownLists).forEach((key) => {
          if (config.dropdownListKeys.includes(key)) {
            dropdownData[key] = data.dropdownLists[key];
          }
        });

        if (Object.keys(dropdownData).length > 0) {
          batch.set(
            logsDocRef,
            {
              ...dropdownData,
              [`${logType}_lastUpdated`]: new Date().toISOString(),
              [`${logType}_updatedBy`]: userData.uid,
            },
            { merge: true }
          );
          operationCount++;
        }
      }

      const logsCollectionRef = collection(
        db,
        "logs",
        userId,
        config.collectionName
      );

      // Only update changed or new rows
      const changedRowsArray = data.rows.filter((row) =>
        data.changedRows?.has(row.id)
      );

      // Track new document references to update firebaseId
      const newDocRefs = new Map();

      changedRowsArray.forEach((row) => {
        let rowDocRef;

        if (row.firebaseId) {
          // Update existing document
          rowDocRef = doc(logsCollectionRef, row.firebaseId);
        } else {
          // Create new document with auto-generated ID
          rowDocRef = doc(logsCollectionRef);
          // Store the mapping of internal ID to new Firebase ID
          newDocRefs.set(row.id, rowDocRef.id);
        }

        // Create a clean object without internal tracking fields
        const { id, firebaseId, ...rowData } = row;

        batch.set(rowDocRef, {
          ...rowData,
          lastUpdated: new Date().toISOString(),
          updatedBy: userData.uid,
          createdAt: row.createdAt || new Date().toISOString(),
        });
        operationCount++;
      });

      // Only commit if there are operations
      if (operationCount > 0) {
        await batch.commit();

        // Update rows with new firebaseIds
        const updatedRows = data.rows.map((row) => {
          if (newDocRefs.has(row.id)) {
            return {
              ...row,
              firebaseId: newDocRefs.get(row.id),
            };
          }
          return row;
        });

        // Update cache with updated rows AND dropdown lists
        updateLogsCache(userId, logType, {
          rows: updatedRows,
          dropdownLists: data.dropdownLists || {},
        });

        return { success: true, operationCount, updatedRows };
      } else {
        toast.info("No changes to save");
        return { success: true, operationCount: 0, updatedRows: data.rows };
      }
    } catch (error) {
      console.error(`Error saving ${logType}:`, error);
      toast.error(`Failed to save ${logType}`);
      return { success: false, error };
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  // Delete a single log
  const deleteLog = async (userId, logType, logId, firebaseId) => {
    try {
      const config = LOG_TYPE_CONFIG[logType];
      if (!config) {
        throw new Error(`Unknown log type: ${logType}`);
      }

      setLoading(true);
      setLoadingText(`Deleting ${config.displayName}...`);

      if (firebaseId) {
        const logsCollectionRef = collection(
          db,
          "logs",
          userId,
          config.collectionName
        );
        const rowDocRef = doc(logsCollectionRef, firebaseId);

        // Delete all comments in the subcollection first
        const commentsRef = collection(rowDocRef, "logComments");
        const commentsSnapshot = await getDocs(commentsRef);

        const batch = writeBatch(db);
        commentsSnapshot.docs.forEach((commentDoc) => {
          batch.delete(commentDoc.ref);
        });

        // Commit the batch deletion of comments
        if (!commentsSnapshot.empty) {
          await batch.commit();
        }

        // Then delete the main document
        await deleteDoc(rowDocRef);
      }

      toast.success("Log deleted successfully");

      // Update cache
      const cached = getCachedLogs(userId, logType);
      if (cached) {
        const updatedRows = cached.rows.filter((row) => row.id !== logId);
        updateLogsCache(userId, logType, {
          ...cached,
          rows: updatedRows,
        });
      }

      return { success: true };
    } catch (error) {
      console.error(`Error deleting ${logType}:`, error);
      toast.error(`Failed to delete ${logType}`);
      return { success: false, error };
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  // Generic load function for all log types
  const loadLogs = useCallback(
    async (userId, logType) => {
      // Check cache first
      const cached = getCachedLogs(userId, logType);
      if (cached) {
        return { success: true, data: cached };
      }

      try {
        const config = LOG_TYPE_CONFIG[logType];
        if (!config) {
          throw new Error(`Unknown log type: ${logType}`);
        }

        setLoading(true);
        setLoadingText(`Loading ${config.displayName}...`);

        const result = {
          rows: [],
          dropdownLists: {},
        };

        // Load dropdown lists from main logs document
        const logsDocRef = doc(db, "logs", userId);
        const logsDocSnap = await getDoc(logsDocRef);

        if (logsDocSnap.exists()) {
          const data = logsDocSnap.data();

          // Dynamically extract dropdown lists based on config
          config.dropdownListKeys.forEach((key) => {
            result.dropdownLists[key] = data[key] || [];
          });
        } else {
          // Initialize empty dropdown lists if document doesn't exist
          config.dropdownListKeys.forEach((key) => {
            result.dropdownLists[key] = [];
          });
        }

        // Load all log rows from subcollection
        const logsCollectionRef = collection(
          db,
          "logs",
          userId,
          config.collectionName
        );
        const querySnapshot = await getDocs(logsCollectionRef);

        if (!querySnapshot.empty) {
          const loadedRows = [];
          querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();

            // Create row object with all fields from Firestore
            const row = {
              id: Date.now() + Math.random(), // Local ID for React key
              firebaseId: docSnap.id,
              createdAt: data.createdAt || data.lastUpdated,
            };

            // Add all other fields from the document
            Object.keys(data).forEach((key) => {
              if (key !== "lastUpdated" && key !== "updatedBy") {
                row[key] = data[key] || "";
              }
            });

            loadedRows.push(row);
          });

          // Sort by creation time
          loadedRows.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          );
          result.rows = loadedRows;
        }

        // Update cache - ensure all dropdown lists are included
        updateLogsCache(userId, logType, result);

        return { success: true, data: result };
      } catch (error) {
        console.error(`Error loading ${logType}:`, error);
        return { success: false, error };
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    },
    [getCachedLogs, updateLogsCache]
  );

  // Clear cache for a specific user (useful when switching users)
  const clearCache = useCallback((userId = null) => {
    if (userId) {
      // Clear specific user's cache
      setLogsCache((prev) => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach((key) => {
          if (key.startsWith(`${userId}_`)) {
            delete newCache[key];
          }
        });
        return newCache;
      });
      setTemplatesCache((prev) => {
        const newCache = { ...prev };
        delete newCache[userId];
        return newCache;
      });
    } else {
      // Clear all cache
      setLogsCache({});
      setTemplatesCache({});
    }
  }, []);

  const value = {
    loading,
    loadingText,
    setLoading,
    setLoadingText,
    saveLogs,
    loadLogs,
    deleteLog,
    saveTemplate,
    deleteTemplate,
    loadTemplates,
    getCachedLogs,
    getCachedTemplates,
    getTemplates,
    clearCache,
  };

  return <LogsContext.Provider value={value}>{children}</LogsContext.Provider>;
};
